import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { DataGridComponent } from '@components/common'
import {
  ColumnModel,
  GridComponent,
  RecordClickEventArgs,
} from '@syncfusion/ej2-react-grids'
import { RowSelectEventArgs } from '@syncfusion/ej2-grids'
import { IFaxMessage } from '@dataTypes/fax.type'
import {
  formatFaxHistoryTime,
  formatFaxListDate,
  getFaxDisplayStatus,
  getFaxHistoryRecipient,
  getFaxHistorySender,
  getFaxTransmissionType,
} from '@utils/fax.util'
import type { FaxPreviewHooks } from './useFaxPreview'

type HistoryGridRow = {
  fax_message_id: string
  recipient: string
  sender: string
  direction: string
  directionClass: string
  statusLabel: string
  statusClass: string
  type: string
  sendTime: string
  sendDate: string
  fax: IFaxMessage
}

type Props = {
  hooks: FaxPreviewHooks
}

const FaxHistoryTable: React.FC<Props> = ({ hooks }) => {
  const gridRef = useRef<GridComponent | null>(null)
  const isSyncingRef = useRef(false)

  const gridData: HistoryGridRow[] = useMemo(
    () =>
      hooks.listMessages.map((fax) => {
        const label = hooks.getDisplayLabel(fax)
        const status = getFaxDisplayStatus(fax)
        const directionClass =
          fax.direction === 'Incoming'
            ? 'fax-pill-inbound'
            : 'fax-pill-outbound'
        return {
          fax_message_id: fax.fax_message_id ?? '',
          recipient: getFaxHistoryRecipient(fax, hooks.clinicName, label),
          sender: getFaxHistorySender(fax, hooks.clinicName, label),
          direction: fax.direction === 'Incoming' ? 'Inbound' : 'Outbound',
          directionClass,
          statusLabel: status.label,
          statusClass: `fax-pill-status-${status.tone}`,
          type: getFaxTransmissionType(fax),
          sendTime: formatFaxHistoryTime(fax.date_time_created_at),
          sendDate: formatFaxListDate(fax.date_time_created_at),
          fax,
        }
      }),
    [hooks],
  )

  const columns: ColumnModel[] = useMemo(() => {
    const statusColumn: ColumnModel = {
      field: 'statusLabel',
      headerText: 'Status',
      width: 110,
      template: (row: HistoryGridRow) => (
        <span className={`fax-status-pill ${row.statusClass}`}>
          {row.statusLabel}
        </span>
      ),
    }
    const sendTimeColumn: ColumnModel = {
      field: 'sendTime',
      headerText: 'Send time',
      width: 130,
      template: (row: HistoryGridRow) => (
        <span>
          {row.sendDate} {row.sendTime}
        </span>
      ),
    }

    return [
      { field: 'recipient', headerText: 'Recipient', width: 140 },
      { field: 'sender', headerText: 'Sender', width: 140 },
      {
        field: 'direction',
        headerText: 'Direction',
        width: 110,
        template: (row: HistoryGridRow) => (
          <span className={`fax-direction-pill ${row.directionClass}`}>
            {row.direction}
          </span>
        ),
      },
      statusColumn,
      { field: 'type', headerText: 'Type', width: 120 },
      sendTimeColumn,
    ]
  }, [])

  const handleRecordClick = (args: RecordClickEventArgs): void => {
    const target = args.target as HTMLElement | undefined
    if (
      target?.closest(
        '.e-checkbox-wrapper, .e-gridchkbox, input[type="checkbox"]',
      )
    ) {
      return
    }
    if (args.column?.type === 'checkbox') return
    const row = args.rowData as HistoryGridRow | undefined
    if (row?.fax) hooks.handleRowClick(row.fax)
  }

  const handleRowSelected = useCallback(
    (args: RowSelectEventArgs) => {
      if (isSyncingRef.current) return
      const row = args.data as HistoryGridRow
      if (row?.fax) hooks.toggleRowSelected(row.fax)
    },
    [hooks],
  )

  const syncGridSelection = useCallback(() => {
    const grid = gridRef.current
    const selection = grid?.selectionModule
    if (!grid || !selection) return
    let view: HistoryGridRow[] = []
    try {
      view = (grid.getCurrentViewRecords() as HistoryGridRow[]) ?? []
    } catch {
      return
    }
    const selectedIds = new Set(
      hooks.selectedFaxes.map((f) => f.fax_message_id),
    )
    const desired: number[] = []
    view.forEach((row, index) => {
      if (row.fax_message_id && selectedIds.has(row.fax_message_id)) {
        desired.push(index)
      }
    })
    const current = grid.getSelectedRowIndexes() ?? []
    const alreadySynced =
      desired.length === current.length &&
      desired.every((index) => current.includes(index))
    if (alreadySynced) return
    isSyncingRef.current = true
    try {
      selection.clearSelection()
      if (desired.length > 0) selection.selectRows(desired)
    } catch {
      /* grid not ready */
    }
    isSyncingRef.current = false
  }, [hooks.selectedFaxes])

  useEffect(() => {
    syncGridSelection()
  }, [syncGridSelection, gridData])

  return (
    <div className="fax-datagrid fax-history-table">
      <DataGridComponent
        ref={gridRef}
        data={gridData}
        columns={columns}
        allowPaging={true}
        allowSorting={true}
        showCheckbox={true}
        showCheckboxWidth={24}
        customCSS="fax-history-grid"
        recordClick={handleRecordClick}
        rowSelected={handleRowSelected}
        rowDeselected={handleRowSelected}
        dataBound={syncGridSelection}
        rowDataBound={(args) => {
          const row = args.data as HistoryGridRow
          if (row.fax_message_id === hooks.selectedFaxId) {
            args.row.classList.add('fax-history-row-selected')
          }
        }}
      />
    </div>
  )
}

export default FaxHistoryTable
